import { Components, Theme } from "@mui/material/styles";
import { BRAND_RGB } from "./palette";

/**
 * Component overrides.
 *
 * Everything here exists so a screen does not have to. The panel's ~40 screens
 * each styled their own buttons, inputs and tables inline, which is why the
 * same control looked different on two pages. A rule belongs in this file when
 * it should hold everywhere; a screen keeps `sx` only for what is genuinely
 * about that screen.
 *
 * Two overrides earn a note:
 *
 * - **Tonal secondary buttons.** `contained` + `secondary`/`info` renders as a
 *   tinted button rather than a solid one. Screens hardcode `color="info"` on
 *   Filter and `color="secondary"` on Clear; solid, those competed with the
 *   page's real action. Tinted, they read as what they are, and no screen had
 *   to be touched.
 * - **Icon-only buttons.** `&:has(> svg)` catches the `<Button>`s
 *   that wrap nothing but an icon - the edit control in every table row - and
 *   makes them square and tinted instead of a solid brand-red pill repeated
 *   twenty times down the page. Same reason: one rule, forty screens.
 */
const components = (theme: Theme): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      },
      // The old scrollbar was a 10px light-grey track with a mid-grey thumb,
      // drawn at full strength beside the sidebar on every page.
      "*::-webkit-scrollbar": { width: 10, height: 10 },
      "*::-webkit-scrollbar-track": { background: "transparent" },
      "*::-webkit-scrollbar-thumb": {
        background: theme.palette.grey[300],
        borderRadius: 8,
        border: "3px solid transparent",
        backgroundClip: "content-box",
      },
      "*::-webkit-scrollbar-thumb:hover": { background: theme.palette.grey[400], backgroundClip: "content-box" },
      // react-perfect-scrollbar draws its own rails over the sidebar.
      ".ps__rail-x, .ps__rail-y": { opacity: "0 !important" as any },
    },
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: "8px 16px",
        minHeight: 40,
        transition: "background-color .15s ease, border-color .15s ease, color .15s ease",
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px rgba(${BRAND_RGB}, 0.24)`,
        },
        // An icon with no label: square it off and quiet it down. Matched on
        // a *direct* svg child, which is the one spelling that is unambiguous.
        // A `startIcon` cannot be used here - CSS cannot see a text node, so
        // `<Button startIcon={<X/>}>Filter</Button>` and
        // `<Button startIcon={<X/>} />` are indistinguishable by selector, and
        // the rule squashed every labelled button to 36px. Icon-only buttons
        // are written `<Button><Icon/></Button>` throughout.
        "&:has(> svg)": {
          minWidth: 36,
          width: 36,
          height: 36,
          minHeight: 36,
          padding: 0,
        },
      },
      sizeSmall: { minHeight: 32, padding: "5px 12px", fontSize: "0.8125rem" },
      sizeLarge: { minHeight: 48, padding: "12px 22px", fontSize: "0.9375rem" },
      contained: {
        boxShadow: "none",
        "&:hover": { boxShadow: "none" },
      },
      containedPrimary: {
        "&:hover": { backgroundColor: theme.palette.primary.dark },
        // The row-action icon button. Solid brand red twenty times down a
        // table is noise; tinted, it still reads as the row's action.
        "&:has(> svg)": {
          backgroundColor: `rgba(${BRAND_RGB}, 0.10)`,
          color: theme.palette.primary.main,
          "&:hover": { backgroundColor: `rgba(${BRAND_RGB}, 0.18)` },
        },
      },
      // "Clear filters" and its kind: present, but not competing with the
      // page's real action.
      containedSecondary: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.text.primary,
        "&:hover": { backgroundColor: theme.palette.grey[200] },
      },
      // "Filter" and its kind. Solid rather than tinted, because eleven of
      // these carry an inline `color: "white"` that a tinted background would
      // render as white-on-near-white.
      containedInfo: {
        backgroundColor: theme.palette.secondary.dark,
        color: theme.palette.common.white,
        "&:hover": { backgroundColor: "#146f79" },
      },
      containedSuccess: { "&:hover": { backgroundColor: theme.palette.success.dark } },
      containedError: { "&:hover": { backgroundColor: theme.palette.error.dark } },
      outlined: {
        borderColor: theme.palette.grey[300],
        color: theme.palette.text.primary,
        "&:hover": {
          borderColor: theme.palette.grey[400],
          backgroundColor: theme.palette.grey[50],
        },
      },
      text: {
        "&:hover": { backgroundColor: theme.palette.grey[100] },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        color: theme.palette.text.secondary,
        "&:hover": { backgroundColor: theme.palette.grey[100], color: theme.palette.text.primary },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px rgba(${BRAND_RGB}, 0.24)`,
        },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        backgroundColor: theme.palette.common.white,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.grey[300] },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.grey[400] },
        "&.Mui-focused": {
          boxShadow: `0 0 0 3px rgba(${BRAND_RGB}, 0.16)`,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
        },
        "&.Mui-disabled": { backgroundColor: theme.palette.grey[50] },
      },
      // The panel's default input was 56px tall, which is a mobile touch
      // target on a screen that shows three filters and a table.
      input: { padding: "12px 14px", fontSize: "0.875rem", height: 20 },
      inputSizeSmall: { padding: "8px 12px", height: 20 },
      multiline: { padding: 0 },
    },
  },

  // An Autocomplete pads its outlined root by 9px and its input by 7.5px, on
  // top of the 20px line above - 53px tall, so a tag picker sat taller than the
  // TextField beside it. 4.5px of root padding brings it back to 44px, and
  // small chips (24px + 3px margins) fit inside that same line once values are
  // picked, so the field doesn't grow on its first tag.
  MuiAutocomplete: {
    defaultProps: { ChipProps: { size: "small" } },
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root:not(.MuiInputBase-sizeSmall)": { paddingTop: 4.5, paddingBottom: 4.5 },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        "&.Mui-focused": { color: theme.palette.primary.main },
      },
      outlined: {
        transform: "translate(14px, 13px) scale(1)",
        "&.MuiInputLabel-shrink": { transform: "translate(14px, -7px) scale(0.78)" },
        "&.MuiInputLabel-sizeSmall": { transform: "translate(12px, 9px) scale(1)" },
        "&.MuiInputLabel-sizeSmall.MuiInputLabel-shrink": {
          transform: "translate(12px, -7px) scale(0.78)",
        },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: { root: { marginLeft: 2, fontSize: "0.75rem" } },
  },

  MuiContainer: {
    styleOverrides: {
      // Screens wrap their content in `<Container maxWidth={false}>`. Its
      // default gutters indented every table and form 24px past the page
      // header that sits above them; the page already has its own padding.
      root: { paddingLeft: "0 !important", paddingRight: "0 !important" },
    },
  },

  MuiSelect: {
    styleOverrides: { select: { display: "flex", alignItems: "center" } },
  },

  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" },
      rounded: { borderRadius: 12 },
      elevation1: { boxShadow: "0 1px 2px rgba(16,24,40,.06), 0 1px 3px rgba(16,24,40,.10)" },
      elevation8: { boxShadow: "0 12px 32px -8px rgba(16,24,40,.18)" },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: "0 12px 32px -8px rgba(16,24,40,.18)",
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "0.875rem",
        borderRadius: 6,
        margin: "2px 6px",
        minHeight: 36,
        "&.Mui-selected": {
          backgroundColor: `rgba(${BRAND_RGB}, 0.08)`,
          color: theme.palette.primary.main,
          fontWeight: 600,
          "&:hover": { backgroundColor: `rgba(${BRAND_RGB}, 0.12)` },
        },
      },
    },
  },

  MuiTableContainer: {
    styleOverrides: {
      root: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: "12px 16px",
        fontSize: "0.875rem",
      },
      head: {
        backgroundColor: theme.palette.grey[50],
        color: theme.palette.text.secondary,
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:last-child td, &:last-child th": { borderBottom: 0 },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: { borderTop: `1px solid ${theme.palette.divider}` },
      selectLabel: { fontSize: "0.8125rem", color: theme.palette.text.secondary },
      displayedRows: { fontSize: "0.8125rem", color: theme.palette.text.secondary },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme: t }) => ({
        borderRadius: 6,
        fontWeight: 600,
        fontSize: "0.75rem",
        // State colours read as a label, not as a button. MUI only exposes
        // `filledPrimary`/`filledSecondary` as override slots, so the rest are
        // matched by class.
        "&.MuiChip-filled": {
          "&.MuiChip-colorSuccess": {
            backgroundColor: t.palette.success.lighter,
            color: t.palette.success.dark,
          },
          "&.MuiChip-colorError": {
            backgroundColor: t.palette.error.lighter,
            color: t.palette.error.dark,
          },
          "&.MuiChip-colorWarning": {
            backgroundColor: t.palette.warning.lighter,
            color: t.palette.warning.dark,
          },
          "&.MuiChip-colorInfo": {
            backgroundColor: t.palette.info.lighter,
            color: t.palette.info.dark,
          },
          "&.MuiChip-colorPrimary": {
            backgroundColor: t.palette.primary.lighter,
            color: t.palette.primary.dark,
          },
          "&.MuiChip-colorSecondary": {
            backgroundColor: t.palette.secondary.lighter,
            color: t.palette.secondary.dark,
          },
          "&.MuiChip-colorDefault": {
            backgroundColor: t.palette.grey[100],
            color: t.palette.text.secondary,
          },
        },
      }),
      sizeSmall: { height: 22 },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 42, borderBottom: `1px solid ${theme.palette.divider}` },
      indicator: { height: 2, borderRadius: 2, backgroundColor: theme.palette.primary.main },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 42,
        padding: "10px 14px",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: theme.palette.text.secondary,
        "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 600 },
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 14, boxShadow: "0 24px 48px -12px rgba(16,24,40,.25)" },
    },
  },

  MuiDialogTitle: {
    styleOverrides: { root: { fontSize: "1.0625rem", fontWeight: 600, padding: "20px 24px 12px" } },
  },

  MuiAlert: {
    styleOverrides: { root: { borderRadius: 10, fontSize: "0.875rem" } },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: theme.palette.grey[900],
        fontSize: "0.75rem",
        borderRadius: 6,
        padding: "6px 10px",
      },
    },
  },

  MuiDivider: {
    styleOverrides: { root: { borderColor: theme.palette.divider } },
  },

  MuiCard: {
    styleOverrides: { root: { backgroundImage: "none" } },
  },

  MuiCheckbox: {
    styleOverrides: { root: { borderRadius: 6 } },
  },

  MuiSwitch: {
    styleOverrides: {
      root: { padding: 8 },
      track: { borderRadius: 12 },
    },
  },

  MuiSkeleton: {
    styleOverrides: { root: { borderRadius: 6, backgroundColor: theme.palette.grey[100] } },
  },

  MuiLink: {
    styleOverrides: {
      root: { color: theme.palette.secondary.dark, textDecorationColor: "transparent" },
    },
  },
});

export default components;
